/**
 * Text obfuscation.
 *
 * Rule string syntax:
 *   'R0' = hide all characters (replace with *)
 *   'R4' = show last 4 characters, mask the rest
 *   'L4' = show first 4 characters, mask the rest
 *   Direction: R = show from right, L = show from left
 *   Number: how many characters to reveal
 */

import { useAuthStore } from '@/store/authStore';
import { getMountConfig } from '@/mountConfig';
import { getRawUserFunctionList } from './permissionsApi';

/**
 * Standalone-mode session check. Returns `true` when the React auth store
 * confirms an authenticated session, `false` otherwise (idle / loading /
 * unauthenticated / error).
 *
 * Embedded mode (AngularJS host) is excluded — the authStore isn't the
 * source of truth there, and the AngularJS shell blocks rendering until its
 * own auth flow has settled, so this guard would over-mask after AngularJS
 * already cleared the user.
 */
function isAuthenticatedSession(): boolean {
  try {
    if (getMountConfig().mode !== 'standalone') return true;
    return useAuthStore.getState().status === 'authenticated';
  } catch {
    // Mount config / store unavailable (tests, SSR) — treat as authenticated
    // so the existing cache-based path makes the decision.
    return true;
  }
}

/**
 * Obfuscate `text` per the given mask rule.
 *
 * @param text - The text to obfuscate
 * @param rule - Rule string like 'R0', 'R4', 'L4'
 */
export function obfuscateText(text: string, rule: string): string {
  if (!text || !rule) return text;

  const ruleDirection = rule[0];
  const ruleCharCount = Number(rule.slice(1));

  let returnString = '';

  if (ruleDirection === 'R') {
    const endIndex = text.length - ruleCharCount > 0 ? text.length - ruleCharCount : 0;
    returnString = text.slice(endIndex, text.length);
    for (let i = 0; i < endIndex; i++) {
      returnString = '*' + returnString;
    }
  } else if (ruleDirection === 'L') {
    const startIndex = ruleCharCount;
    returnString = text.slice(0, startIndex);
    for (let i = 0; i < text.length - ruleCharCount; i++) {
      returnString += '*';
    }
  }

  return returnString;
}

/**
 * Mask `text` based on whether the current user holds the given permission:
 *
 *   • permission held with a role suffix (e.g. `=R4`) → mask per role
 *   • permission held with no role suffix              → full text (admin / cleared)
 *   • permission not held                              → fully masked (R0)
 *   • permission cache not loaded yet                  → fully masked (R0) — safe
 *     default so values NEVER leak even briefly during the cache-load window.
 *     The next render after the cache lands applies the real permission rule.
 *
 * Callers should ensure rendering only happens once `AuthGate` reports
 * authenticated (the cache is populated by then in normal flows), so the
 * "cache null" branch is a defensive backstop rather than a steady state.
 *
 * @param text - The text to potentially obfuscate
 * @param permissionString - The raw permission string to check (e.g., 'Audit:ViewLoanNumber')
 */
export function obfuscateTextByPermission(text: string, permissionString: string): string {
  if (!text) return text;

  // Belt-and-suspenders safety: mask whenever the user isn't confirmed
  // authenticated OR the permission cache hasn't been populated. Either
  // state means we can't reliably decide what the user is allowed to see,
  // so we default to fully masked rather than leak the raw value for even
  // one frame (page reload, expired session, cache cleared, etc.).
  if (!isAuthenticatedSession() || getRawUserFunctionList() == null) {
    return obfuscateText(text, 'R0');
  }

  const result = getPermission(permissionString);

  if (result.validPermission) {
    if (result.role) {
      return obfuscateText(text, result.role);
    } else {
      return text;
    }
  } else {
    return obfuscateText(text, 'R0');
  }
}

/**
 * Look up a permission string in the cached user function list and return
 * whether it's present plus any role suffix (e.g. `Vendor:ViewSSN=R4` → `R4`).
 */
function getPermission(permissionString: string): { validPermission: boolean; role?: string } {
  const userFunctionList = getRawUserFunctionList();
  if (!userFunctionList || !permissionString) {
    return { validPermission: false };
  }

  for (let i = 0; i < userFunctionList.length; i++) {
    const userFunction = userFunctionList[i];
    const delimiterIndex = userFunction.indexOf('=');
    const userFunctionLeftSide =
      delimiterIndex > 0 ? userFunction.slice(0, delimiterIndex) : userFunction;
    const userFunctionRightSide =
      delimiterIndex > 0 ? userFunction.slice(delimiterIndex + 1) : undefined;

    if (permissionString === userFunctionLeftSide) {
      return { validPermission: true, role: userFunctionRightSide };
    }
  }

  return { validPermission: false };
}
